import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import logo from '../assets/logo.png';
import { useVehicles } from '../context/VehiclesContext';
import { auth } from '../firebase';

function ShellHeader({ authUser }) {
  const location = useLocation();
  const activeAdminSection = new URLSearchParams(location.search).get('section') || 'inventory';
  const isAdminRoute = location.pathname === '/admin';
  const [isNavOpen, setIsNavOpen] = useState(false);
  const { vehicles } = useVehicles();
  const [expandedSections, setExpandedSections] = useState({
    usedCars: false,
    usedVans: false,
    services: false,
  });

  const usedCarMakes = useMemo(() => {
    const carMakes = vehicles
      .filter((vehicle) => (vehicle.vehicleType || 'car').toLowerCase() === 'car')
      .map((vehicle) => vehicle.make)
      .filter(Boolean);

    return [...new Set(carMakes)].sort((a, b) => a.localeCompare(b));
  }, [vehicles]);

  const usedVanMakes = useMemo(() => {
    const vanMakes = vehicles
      .filter((vehicle) => (vehicle.vehicleType || 'car').toLowerCase() === 'van')
      .map((vehicle) => vehicle.make)
      .filter(Boolean);

    return [...new Set(vanMakes)].sort((a, b) => a.localeCompare(b));
  }, [vehicles]);

  const toggleSection = (sectionName) => {
    setExpandedSections((current) => ({
      ...current,
      [sectionName]: !current[sectionName],
    }));
  };

  useEffect(() => {
    document.body.style.overflow = isNavOpen ? 'hidden' : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [isNavOpen]);

  const handleSignOut = async () => {
    await signOut(auth);
    setIsNavOpen(false);
  };

  return (
    <>
      {location.pathname !== '/' && (
        <div className="shell-logo-slot">
          <Link
            to="/"
            className="shell-logo-link"
            onClick={() => setIsNavOpen(false)}
            aria-label="Go to homepage"
          >
            <img src={logo} alt="MJ Motors" className="shell-logo-image" />
          </Link>
        </div>
      )}

      <button
        type="button"
        className={`hamburger-btn ${isNavOpen ? 'open' : ''}`}
        onClick={() => setIsNavOpen((current) => !current)}
        aria-label={isNavOpen ? 'Close navigation panel' : 'Open navigation panel'}
        aria-expanded={isNavOpen}
      >
        <span />
        <span />
        <span />
      </button>

      <button
        type="button"
        className={`slideout-overlay ${isNavOpen ? 'open' : ''}`}
        onClick={() => setIsNavOpen(false)}
        aria-label="Close navigation panel"
      />

      <aside className={`slideout-panel ${isNavOpen ? 'open' : ''}`} aria-hidden={!isNavOpen}>
        <div className="slideout-header">
          <h2>Navigation</h2>
          <button
            type="button"
            className="slideout-close-btn"
            onClick={() => setIsNavOpen(false)}
            aria-label="Close navigation panel"
          >
            ×
          </button>
        </div>

        <nav className="slideout-nav">
          <Link to="/" onClick={() => setIsNavOpen(false)}>Home</Link>

          <div className="slideout-group">
            <button
              type="button"
              className="slideout-group-toggle"
              onClick={() => toggleSection('usedCars')}
              aria-expanded={expandedSections.usedCars}
            >
              Used Cars
            </button>
            {expandedSections.usedCars && (
              <div className="slideout-group-items">
                {usedCarMakes.length === 0 && <span className="slideout-empty">No car makes available</span>}
                {usedCarMakes.map((make) => (
                  <Link
                    key={`car-${make}`}
                    to={`/search?type=car&make=${encodeURIComponent(make)}`}
                    onClick={() => setIsNavOpen(false)}
                  >
                    {make}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="slideout-group">
            <button
              type="button"
              className="slideout-group-toggle"
              onClick={() => toggleSection('usedVans')}
              aria-expanded={expandedSections.usedVans}
            >
              Used Vans
            </button>
            {expandedSections.usedVans && (
              <div className="slideout-group-items">
                {usedVanMakes.length === 0 && <span className="slideout-empty">No van makes available</span>}
                {usedVanMakes.map((make) => (
                  <Link
                    key={`van-${make}`}
                    to={`/search?type=van&make=${encodeURIComponent(make)}`}
                    onClick={() => setIsNavOpen(false)}
                  >
                    {make}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link to="/finance" onClick={() => setIsNavOpen(false)}>Finance</Link>

          <div className="slideout-group">
            <button
              type="button"
              className="slideout-group-toggle"
              onClick={() => toggleSection('services')}
              aria-expanded={expandedSections.services}
            >
              Services
            </button>
            {expandedSections.services && (
              <div className="slideout-group-items">
                <Link to="/services/part-exchange" onClick={() => setIsNavOpen(false)}>Part Exchange</Link>
                <Link to="/sell-your-car" onClick={() => setIsNavOpen(false)}>Sell Your Car</Link>
                <Link to="/services/servicing" onClick={() => setIsNavOpen(false)}>Servicing</Link>
                <Link to="/services/warranty" onClick={() => setIsNavOpen(false)}>Warranty</Link>
              </div>
            )}
          </div>

          <Link to="/about-us" onClick={() => setIsNavOpen(false)}>About Us</Link>
          <Link to="/contact" onClick={() => setIsNavOpen(false)}>Contact</Link>

          {authUser && <Link to="/admin" onClick={() => setIsNavOpen(false)}>Admin</Link>}
          {authUser && (
            <div className="admin-mobile-links">
              <Link
                to="/admin?section=inventory"
                className={isAdminRoute && activeAdminSection === 'inventory' ? 'admin-mobile-link active' : 'admin-mobile-link'}
                onClick={() => setIsNavOpen(false)}
              >
                Manage Inventory
              </Link>
              <Link
                to="/admin?section=add-vehicle"
                className={isAdminRoute && activeAdminSection === 'add-vehicle' ? 'admin-mobile-link active' : 'admin-mobile-link'}
                onClick={() => setIsNavOpen(false)}
              >
                Add Vehicle
              </Link>
              <Link
                to="/admin?section=testimonials"
                className={isAdminRoute && activeAdminSection === 'testimonials' ? 'admin-mobile-link active' : 'admin-mobile-link'}
                onClick={() => setIsNavOpen(false)}
              >
                Manage Testimonials
              </Link>
              <Link
                to="/admin?section=users"
                className={isAdminRoute && activeAdminSection === 'users' ? 'admin-mobile-link active' : 'admin-mobile-link'}
                onClick={() => setIsNavOpen(false)}
              >
                Manage Users
              </Link>
            </div>
          )}
          {authUser && (
            <button type="button" className="slideout-signout-btn" onClick={handleSignOut}>
              Sign Out
            </button>
          )}
        </nav>
      </aside>
    </>
  );
}

export default ShellHeader;
